import { ReactNode, useMemo } from "react";

import { DEFAULT_DEPARTMENT_OPTIONS } from "../../pages/LandingPage/constants";

import { useUIStateContext } from "../UIStateContext";

import { UserContext } from "./UserContext";

import { useUserInitialization } from "./hooks/useUserInitialization";
import { useUserIntegrations } from "./hooks/useUserIntegrations";
import { useUserSetupInfo } from "./hooks/useUserSetupInfo";
import { useUserState } from "./hooks/useUserState";

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps): JSX.Element => {
  const { setNetworkErrorToast } = useUIStateContext();

  const userState = useUserState();

  const {
    user,
    setUser,
    isDevUser,
    setIsDevUser,
    permissions,
    setPermissions,
    selected,
    setSelected,
    defaultStore,
    setDefaultStore,
    tableTitleInfo,
    setTableTitleInfo,
    tableTitleInfoForTab,
    setTableTitleInfoForTab,
    iamUserState,
    setIamUserState,
    evaluatedResults,
    setEvaluatedResults,
    isFetchingUserData,
    setIsFetchingUserData,
    setupListNavInfoByDepartment,
    setSetupListNavInfoByDepartment,
    favorites,
    setFavorites,
    initialFavorites,
    setInitialFavorites,
    resolvedLaunchContext,
    setResolvedLaunchContext,
  } = userState;

  useUserInitialization({
    user,
    setUser,
    isDevUser,
    setIsDevUser,
    setPermissions,
    selected,
    setSelected,
    setDefaultStore,
    setIamUserState,
    setIsFetchingUserData,
    setSetupListNavInfoByDepartment,
    setFavorites,
    setInitialFavorites,
    setNetworkErrorToast,
    setResolvedLaunchContext,
  });

  const { fullName, headerLabel, setupBaseRequestInfo } = useUserSetupInfo({
    user,
    selected,
    iamUserState,
    setupListNavInfoByDepartment,
    resolvedLaunchContext,
  });

  useUserIntegrations({
    loginId: user.user.loginId,
    storeId: selected.store?.value,
    storeName: selected.store?.label,
    enterpriseId: resolvedLaunchContext.effectiveEnterpriseId,
    enterpriseName: user.enterprise?.name, //NOTE - we may also want to use resolvedLaunchContext.effectiveEnterpriseName if available
  });

  const value = useMemo(
    () => ({
      user,
      permissions,
      setPermissions,

      departments: DEFAULT_DEPARTMENT_OPTIONS,

      selected,
      defaultStore,
      setSelected,

      fullName,
      headerLabel,

      tableTitleInfo,
      setTableTitleInfo,

      tableTitleInfoForTab,
      setTableTitleInfoForTab,

      iamUserState,
      setIamUserState,

      evaluatedResults,
      setEvaluatedResults,

      setupBaseRequestInfo,

      isFetchingUserData,
      setIsFetchingUserData,

      setupListNavInfoByDepartment,
      setSetupListNavInfoByDepartment,

      favorites,
      setFavorites,

      initialFavorites,
      setInitialFavorites,

      resolvedLaunchContext,
      setResolvedLaunchContext,
    }),
    [
      user,
      permissions,
      selected,
      defaultStore,
      fullName,
      headerLabel,
      tableTitleInfo,
      tableTitleInfoForTab,
      iamUserState,
      evaluatedResults,
      setupBaseRequestInfo,
      isFetchingUserData,
      setupListNavInfoByDepartment,
      favorites,
      initialFavorites,
      resolvedLaunchContext,
      setPermissions,
      setSelected,
      setTableTitleInfo,
      setTableTitleInfoForTab,
      setIamUserState,
      setEvaluatedResults,
      setIsFetchingUserData,
      setSetupListNavInfoByDepartment,
      setFavorites,
      setInitialFavorites,
      setResolvedLaunchContext,
    ],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
